import { useContext, useState } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { GeneralLedgerContext } from "../../providers/GeneralLedgerProvider";
import {
  AccordionItemsRendererProps,
  ChartOfAccountTableStrings,
  GeneralLedgerChartAccountListAccodianType,
  JournalEntryStrings,
  JournalTableStrings,
  ListingItemsStrings,
} from "./types";
import { CollapseIcon, Download, ExpandIcon } from "../../assets";
import { AccordianSkeleton } from "../../components/Skeleton/ReportSkeleton";
import AccordionItemsRenderer from "./components/AccordionItemsRenderer";
import AddEntryDialog from "./components/AddEntryDialog";
import { EmptyState } from "../../components/ui/empty-state";
import { ChevronLeft, Table } from "lucide-react";
import ListingTableRow from "./components/ListingTableRow";
import CustomDatePicker from "../../components/common/CustomDatePicker";
import { formatUniversalNumber } from "../../lib/utils";
import { UIContext } from "../../providers/UIProvider";
import { TableSkeleton } from "../../components/Skeleton/TableSkeleton";

type GeneralLedgerTabsProps = {
  withExpandAllButton: boolean;
  withDownloadButton: boolean;
  stringOverrides: {
    chartOfAccountTable: ChartOfAccountTableStrings;
    journalTable: JournalTableStrings;
    journalEntry: JournalEntryStrings;
    listing: ListingItemsStrings;
  };
};

const GeneralLedgerTabs = ({
  withExpandAllButton,
  withDownloadButton,
  stringOverrides,
}: GeneralLedgerTabsProps) => {
  const {
    journalData,
    chartofaccountsData,
    isLoading,
    activeTab,
    isExpanded,
    toggleAccordion,
    handleTabChange,
    expandAllRows,
    collapseAllRows,
    areAllRowsExpanded,
    journalPagination,
    setJournalPagination,
    chartListing,
    handleChangeChartListing,
    listingData,
    handleDownload,
    isDownloading,
    getJournalistData,
  } = useContext(GeneralLedgerContext);
  const { businessId } = useContext(UIContext);

  let chartData = chartofaccountsData || [];

  if (chartData.length === 0) {
    chartData = [];
  }

  const validateItemsHaveValidChildren = (
    items: GeneralLedgerChartAccountListAccodianType[]
  ) => {
    return items.map((item) => ({
      ...item,
      children: Array.isArray(item.children) ? item.children : [],
    }));
  };

  const validatedChartData = validateItemsHaveValidChildren(chartData);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [step, setStep] = useState(0);
  const steps = ["Effective Date", "Debit Account", "Credit Account", "Notes"];

  const handleNext = () => {
    if (step === steps.length - 1) {
    } else {
      setStep((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    setStep((prev) => Math.max(prev - 1, 0));
  };

  const ChartOfAccountsColumns: AccordionItemsRendererProps["columns"] = [
    {
      label: stringOverrides?.chartOfAccountTable?.nameColumnHeader || "-",
      className: "",
    },
    {
      label: stringOverrides?.chartOfAccountTable?.typeColumnHeader || "-",
      className: "px-2",
    },
    {
      label: stringOverrides?.chartOfAccountTable?.subtypeColumnHeader || "-",
      className: "px-2",
    },
    {
      label: stringOverrides?.chartOfAccountTable?.balanceColumnHeader || "-",
      className: "text-right pr-4",
    },
    { label: "", className: "flex justify-end pr-4" },
  ];

  const JournalColumns: AccordionItemsRendererProps["columns"] = [
    { label: stringOverrides?.journalTable?.idLabel || "-", className: "" },
    { label: stringOverrides?.journalTable?.dateLabel || "-", className: "" },
    {
      label: stringOverrides?.journalTable?.transactionLabel || "-",
      className: "",
    },
    {
      label: stringOverrides?.journalTable?.accountLabel || "-",
      className: "",
    },
    {
      label: stringOverrides?.journalTable?.debitLabel || "-",
      className: "text-right",
    },
    {
      label: stringOverrides?.journalTable?.creditLabel || "-",
      className: "text-right",
    },
  ];

  return (
    <div className="flex w-full mx-auto">
      <div className={`px-3 py-3 transition-all duration-300 w-full`}>
        <AddEntryDialog
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          setIsModalOpen={setIsModalOpen}
          step={step}
          setStep={setStep}
          handleNext={handleNext}
          handlePrev={handlePrev}
        />
        {/* Tabs */}
        <Tabs
          defaultValue="chartofaccounts"
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <div className="flex justify-between flex-wrap items-center mb-4 space-y-2">
            <h1 className="BookEZ_font_body_large BookEZ_font_bold">
              {activeTab == "journal" ? "Journal" : "Chart of Accounts"}
            </h1>
            <div className="flex gap-2 items-center flex-wrap">
              <TabsList className="h-[42px] p-1 max-w-[350px] BookEZ_font_color_gray_2">
                <TabsTrigger
                  value="chartofaccounts"
                  className={`px-3 md:px-5 h-8 focus:ring-0 focus:ring-offset-0`}
                >
                  Charts of Accounts
                </TabsTrigger>
                <TabsTrigger
                  value="journal"
                  className={`px-3 md:px-5 h-8 focus:ring-0 focus:ring-offset-0`}
                >
                  Journal
                </TabsTrigger>
              </TabsList>

              <div className="flex md:flex-nowrap! flex-wrap! gap-3 items-center">
                {/* {activeTab === "chartofaccounts" && showAddAccountButton && (
                  <Button
                    variant="outlineBlack"
                    size="lg"
                    className="flex gap-2 transition-all duration-200"
                    onClick={() => setIsModalOpen(true)}
                  >
                    <Plus className="h-4 w-4 BookEZ_btn_outlined_font_color" />{" "}
                    <span className="md:block hidden">
                      {stringOverrides?.chartOfAccountTable?.addAccountButtonText}
                    </span>
                  </Button>
                )} */}

                {activeTab === "journal" && (
                  <CustomDatePicker
                    onDateRangeChange={(startDate, endDate) => {
                      if (businessId && getJournalistData) {
                        setJournalPagination({
                          ...journalPagination,
                          page: 1,
                        });
                        getJournalistData(businessId, 1, startDate, endDate);
                      }
                    }}
                    defaultDateRangeOption="this-month"
                  />
                )}

                {withExpandAllButton &&
                  activeTab === "chartofaccounts" &&
                  !chartListing.isListingShow && (
                    <>
                      {areAllRowsExpanded(activeTab) ? (
                        <Button
                          variant="ghost"
                          size="lg"
                          onClick={() => collapseAllRows(activeTab)}
                          className="flex items-center justify-center gap-x-3 px-4! transition-all duration-200"
                          disabled={isLoading}
                        >
                          <CollapseIcon className="mt-1  pr-1 BookEZ_font_color_gray_2" />{" "}
                          Collapse All Rows
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="lg"
                          onClick={() => expandAllRows(activeTab)}
                          className="flex items-center justify-center gap-x-3 px-4! transition-all duration-200"
                          disabled={isLoading}
                        >
                          <ExpandIcon className="mt-1 pr-1 BookEZ_font_color_gray_2" />{" "}
                          Expand All Rows
                        </Button>
                      )}
                    </>
                  )}

                {withDownloadButton &&
                  activeTab === "chartofaccounts" &&
                  !chartListing.isListingShow && (
                    <Button
                      variant="ghost"
                      size="lg"
                      className="flex gap-2 px-4! transition-all duration-200"
                      disabled={
                        isDownloading ||
                        (activeTab == "chartofaccounts" &&
                          chartData.length == 0)
                      }
                      onClick={handleDownload}
                    >
                      <Download className="h-4 w-4 BookEZ_font_color_gray_2" />{" "}
                      Download
                    </Button>
                  )}
              </div>
            </div>
          </div>

          {isLoading ? (
            <AccordianSkeleton />
          ) : (
            <>
              <TabsContent value="chartofaccounts" className="mt-0">
                <div className="BookEZ_card_radius border BookEZ_card_border_color overflow-x-auto">
                  {/* Show empty state if no chart data */}
                  {!chartData.length ? (
                    <EmptyState title="No chart of accounts found" />
                  ) : (
                    <div className="min-w-[800px]">
                      {chartListing.isListingShow ? (
                        <div className="">
                          <div className="flex items-center gap-3 px-3 py-4">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="shrink-0"
                              onClick={() => handleChangeChartListing(false)}
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <div>
                              <h2 className="BookEZ_font_body_size BookEZ_font_bold BookEZ_font_color_gray_2">
                                {chartListing.listingId}
                              </h2>
                              <p className="BookEZ_font_body_size BookEZ_font_color_gray_2">
                                Current balance{" "}
                                <span className="BookEZ_font_body_size BookEZ_font_bold BookEZ_font_color_gray_2">
                                  {formatUniversalNumber(chartListing.balance)}
                                </span>
                              </p>
                            </div>
                            <div className="w-6" /> {/* spacer for alignment */}
                          </div>

                          <div className="overflow-x-auto w-full border BookEZ_accordion_border_color overflow-hidden BookEZ_card_radius">
                            {listingData.length > 0 ? (
                              <ListingTableRow listingData={listingData} />
                            ) : (
                              <EmptyState title="No listing data found" />
                            )}
                          </div>
                        </div>
                      ) : (
                        <AccordionItemsRenderer
                          columns={ChartOfAccountsColumns}
                          chartsAccountListItems={validatedChartData}
                          tabId="chartofaccounts"
                          isExpanded={isExpanded}
                          toggleAccordion={toggleAccordion}
                          showEditButton={true}
                          onEdit={(item) => {}}
                          onSave={(item) => {}}
                        />
                      )}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="journal" className="mt-0">
                <div
                  className={`rounded-lg ${
                    journalData?.length <= 0 ? "border" : ""
                  }  BookEZ_card_border_color overflow-x-auto`}
                >
                  {isLoading ? (
                    <TableSkeleton />
                  ) : journalData?.length <= 0 ? (
                    <EmptyState title="No journal entries found" />
                  ) : (
                    <div className="">
                      <AccordionItemsRenderer
                        columns={JournalColumns}
                        journalItems={journalData}
                        tabId="journal"
                        isExpanded={isExpanded}
                        toggleAccordion={toggleAccordion}
                        journalPagination={journalPagination}
                        setJournalPagination={setJournalPagination}
                        JournalModalStrings={stringOverrides?.journalEntry}
                      />
                    </div>
                  )}
                </div>
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default GeneralLedgerTabs;
